import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, Redirect } from "react-router-dom";
import { Box, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import clsx from "clsx";
import MaterialTable from "material-table";
import { MOBILE_LOG_ROUTES_TABLE_COLUMNS } from "constants/ui-constants";
import { getColumns, getLocalization } from "util/table-utils";
import { mapTableData } from "util/helpers";
import Navbar from "components/Navbar";
import DiagnosticsBar from "components/Masterbar/DiagnosticsBar";
import DarkLayout from "components/Shared/DarkLayout";
import { Input } from "components/Shared/mui-formik-inputs";
import {
  getDiagnosticsSettings,
  getMobileLogRoutes,
  selectDiagnosticsLoading,
  selectDiagnosticsSettings,
  selectMobileLogRoutes,
  selectMobileLogRoutesPagination,
} from "redux/slices/diagnosticsSlice";
import { selectUser } from "redux/slices/userSlice";
import { PATHS } from "util/appConstants";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _basicdetailhead: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: "41px",
  },
  _field: {
    marginRight: "48px",
    marginBottom: "16px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
  },
  _detail: {
    color: "#F5F5F5",
    font: "normal normal normal 18px/24px Roboto",
  },
  tableSection: {
    marginTop: "80px",
  },
  filters: {
    marginTop: "24px",
    marginBottom: "16px",
    "& .MuiFilledInput-root": {
      backgroundColor: "#1F1F1F",
    },
    "& .MuiFilledInput-root:hover": {
      backgroundColor: "#262626",
    },
    "& .MuiFilledInput-root.Mui-focused": {
      backgroundColor: "#262626",
    },
    "& .MuiInputBase-input": {
      color: "#F5F5F5",
    },
    "& .MuiFormLabel-root": {
      color: "#ADADAD",
    },
    "& .MuiFormLabel-root.Mui-focused": {
      color: "#6F9CEB",
    },
    "& .MuiFormHelperText-root": {
      color: "#ADADAD",
    },
  },
});

const tableTitle = "MOBILE LOG ROUTES";

const isDiagnosticsVisible = (user) =>
  user?.isSuperAdmin && import.meta.env.VITE_SHOW_SUPPORT === "true";

const DiagnosticsMobile = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const loading = useSelector(selectDiagnosticsLoading);
  const settings = useSelector(selectDiagnosticsSettings);
  const mobileLogRoutes = useSelector(selectMobileLogRoutes);
  const pagination = useSelector(selectMobileLogRoutesPagination);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [routeId, setRouteId] = useState("");

  useEffect(() => {
    if (!isDiagnosticsVisible(user)) {
      return;
    }
    dispatch(getDiagnosticsSettings());
  }, [dispatch, user]);

  useEffect(() => {
    if (!isDiagnosticsVisible(user)) {
      return;
    }
    dispatch(
      getMobileLogRoutes({
        page: page + 1,
        limit: pageSize,
        route_id: routeId || undefined,
      }),
    );
  }, [dispatch, user, page, pageSize, routeId]);

  if (!isDiagnosticsVisible(user)) {
    return <Redirect to={PATHS.admin.supplierGroups} />;
  }

  const mobile = settings?.settings?.mobile || {};
  const forceUpdate = mobile.force_update_after || {};
  const forceUpdateLabel =
    forceUpdate.value != null
      ? `${forceUpdate.value} ${t(forceUpdate.unit === "hours" ? "Hours" : "Days")}`
      : "-";

  return (
    <>
      <Navbar />
      <DiagnosticsBar />
      <DarkLayout doublebar loading={loading && !settings}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {t("Force Update Settings")}
          </Typography>
          <Box className={classes._buttonbox} component="div">
            <EditIcon
              onClick={() => history.push(PATHS.diagnostics.mobileSettings)}
              className={classes._edit}
            />
            <Typography component="span" className={clsx(classes._edittext, "edittag")}>
              {t("Edit")}
            </Typography>
          </Box>
        </div>

        <div className={classes._basicdetailhead}>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Force update after")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {forceUpdateLabel}
            </Typography>
          </div>
          <div className={classes._field}>
            <Typography className={classes._head} variant="h6">
              {t("Min version")}
            </Typography>
            <Typography className={classes._detail} variant="h6">
              {mobile.min_version || "-"}
            </Typography>
          </div>
        </div>

        <Box className={classes.tableSection}>
          <Typography className={classes._infoheading} variant="h5">
            {t("Mobile Logs")}
          </Typography>
          <Grid container spacing={2} className={classes.filters}>
            <Grid item xs={12} md={3}>
              <Input
                name="route_id"
                label={t("Route ID")}
                value={routeId}
                onChange={(e) => {
                  setRouteId(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
          </Grid>
          <div className="custom-table-styles">
            <MaterialTable
              style={{ display: "flex", flexDirection: "column" }}
              isLoading={loading}
              data={mapTableData(mobileLogRoutes)}
              title={t(tableTitle)}
              columns={getColumns(MOBILE_LOG_ROUTES_TABLE_COLUMNS(t), t)}
              onRowClick={(e, rowData) =>
                history.push(
                  PATHS.diagnostics.mobileRouteLogs.replace(":routeId", rowData.route_id),
                )
              }
              localization={getLocalization(t)}
              totalCount={pagination?.total || 0}
              page={page}
              onChangePage={(nextPage) => setPage(nextPage)}
              onChangeRowsPerPage={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              options={{
                pageSize,
                pageSizeOptions: [20, 50, 100],
                actionsColumnIndex: -1,
                searchFieldAlignment: "left",
                showTitle: false,
                search: false,
                sorting: false,
                emptyRowsWhenPaging: false,
                headerStyle: {
                  backgroundColor: "#121212",
                  color: "white",
                  borderBottom: "1px solid #525252",
                  font: "normal normal normal 12px/24px Roboto",
                  fontWeight: "bold",
                },
                cellStyle: {
                  backgroundColor: "#121212",
                  color: "white",
                  border: "none",
                  font: "normal normal normal 12px/24px Roboto",
                  padding: "0 16px",
                },
                searchFieldStyle: {
                  color: "#F5F5F5",
                },
                filterCellStyle: {
                  color: "#F5F5F5",
                },
                rowStyle: { height: "38px", cursor: "pointer" },
              }}
            />
          </div>
        </Box>
      </DarkLayout>
    </>
  );
};

export default DiagnosticsMobile;
